import type { FC } from 'react';

import type { PermissionGroupAssignedUsersRequest } from 'src/apis/authorization-service/use-permission-group-assigned-users';
import { TableActions } from 'src/components/buttons-and-actions/table-actions';
import { Table } from 'src/components/data-display/smart-table';
import { Td, Th } from 'src/components/data-display/table';
import { Link } from 'src/components/navigation/link';
import {
  PageSpinner,
  SpinnerContainer,
} from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import type { SavedReferrer } from 'src/hooks/use-referrer';
import { useToast } from 'src/hooks/use-toasts';

export const UserTableWrapper: FC<{
  savedReferrer: SavedReferrer;
  assignedUsers: PermissionGroupAssignedUsersRequest;
}> = ({ savedReferrer, assignedUsers }) => {
  const notify = useToast();

  // render no table if we cannot fetch users
  if (!assignedUsers) {
    notify({
      type: 'error',
      children: 'Could not fetch assigned users.',
    });
    return null;
  }

  if (!assignedUsers.response) {
    return <PageSpinner />;
  }
  return (
    <SpinnerContainer pending={assignedUsers.pending}>
      <UserTableForExistingData
        assignedUsers={assignedUsers}
        savedReferrer={savedReferrer}
      />
    </SpinnerContainer>
  );
};

const UserTableForExistingData: FC<{
  assignedUsers: Successful<PermissionGroupAssignedUsersRequest>;
  savedReferrer: SavedReferrer;
}> = ({ assignedUsers, savedReferrer }) => {
  return (
    <Table
      data={assignedUsers.response.data._embedded.items}
      data-testid="users-assigned-to-permission-group"
      setId={(user) => user.userId}
      pending={assignedUsers.pending}
      mode="light"
      staticHeader={true}
      cols={[
        {
          key: 'name',
          width: 2,
          head: <Th>Name</Th>,
          body: (user) => (
            <Td>
              {user.firstName} {user.lastName}
            </Td>
          ),
        },
        {
          key: 'email',
          width: 2,
          head: <Th>Email</Th>,
          body: (user) => <Td>{user.email}</Td>,
        },
        {
          key: 'actions',
          width: '7rem',
          head: <Th align="center">Actions</Th>,
          body: (user) => (
            <Td align="center">
              <TableActions>
                <Link
                  data-testid={`user-link-${user.userId}`}
                  {...savedReferrer.getLinkProps(
                    `/settings/organisation/users/${user.userId}`
                  )}
                  target="_blank" // open page in a new tab so as not to interrupt flow
                >
                  Details
                </Link>
              </TableActions>
            </Td>
          ),
        },
      ]}
    />
  );
};
